import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../supabase";
import { ORDER_STATUS_COLORS, buildOrderStatusMap } from "../../utils/orderStatus";
import { useModal } from "../../context/ModalContext";
import Select from "../../components/Select";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const API = process.env.REACT_APP_API_URL || "";

const STATUS_COLORS = ORDER_STATUS_COLORS;

export default function AdminUsers() {
  const { showAlert, showConfirm } = useModal();
  const { t } = useTranslation();
  const STATUS_MAP = buildOrderStatusMap(t);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const planFilter = "all"; // 플랜 필터 화면에서 뺌 (2026-10-19)
  const [sortOrder, setSortOrder] = useState("desc"); // desc: 최신순, asc: 오래된순
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  async function loadUserDetail(user) {
    setSelectedUser(user);
    setDetailLoading(true);
    try {
      const [ordersRes, addrsRes, filesRes, limitsRes] = await Promise.all([
        fetch(`${API}/api/orders?search_type=customer_phone&search_value=&limit=100`).then(r => r.json()),
        fetch(`${API}/api/addresses?user_id=${user.id}`).then(r => r.json()),
        supabase.from("user_files").select("file_size").eq("user_id", user.id).eq("is_deleted", false),
        supabase.from("profiles").select("plan, storage_limit_mb").eq("id", user.id).single(),
      ]);
      const userOrders = (ordersRes.orders || []).filter(o => o.user_id === user.id);
      const totalBytes = (filesRes.data || []).reduce((s, f) => s + (f.file_size || 0), 0);
      const usedMb = Math.round(totalBytes / (1024 * 1024) * 100) / 100;
      const limitMb = limitsRes.data?.storage_limit_mb || 0;
      setUserDetail({
        orders: userOrders,
        addresses: Array.isArray(addrsRes) ? addrsRes : [],
        storage: { usedMb, limitMb, fileCount: (filesRes.data || []).length },
      });
    } catch (e) {
      console.error(e);
      setUserDetail({ orders: [], addresses: [], storage: { usedMb: 0, limitMb: 0, fileCount: 0 } });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    async function loadUsers() {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, email, username, role, plan, is_blocked, created_at, provider")
        .order("created_at", { ascending: false });

      if (!error && Array.isArray(data)) {
        setUsers(data);
      } else {
        console.error("유저 로드 실패:", error);
      }

      setLoading(false);
    }

    loadUsers();
  }, []);

  async function toggleBlock(userId, currentBlocked) {
    const newBlocked = !currentBlocked;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/admin/toggle-block`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ target_user_id: userId, is_blocked: newBlocked }),
      });
      if (!res.ok) {
        showAlert("차단 처리 실패");
        return;
      }
      setUsers(
        users.map((u) =>
          u.id === userId ? { ...u, is_blocked: newBlocked } : u
        )
      );
    } catch (e) {
      console.error(e);
      showAlert("서버 오류");
    }
  }

  const filtered = users.filter((u) => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (
        !u.email?.toLowerCase().includes(q) &&
        !u.username?.toLowerCase().includes(q)
      )
        return false;
    }
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (planFilter !== "all") {
      if (u.role === "admin") return false;
      if ((u.plan || "FREE") !== planFilter) return false;
    }
    return true;
  }).sort((a, b) => {
    const da = new Date(a.created_at || 0).getTime();
    const db = new Date(b.created_at || 0).getTime();
    return sortOrder === "desc" ? db - da : da - db;
  });

  if (loading) return <div>로딩 중…</div>;

  return (
    <div className="admin-page">
      <h2>유저 관리</h2>

      {/* 검색 & 필터 */}
      <div className="admin-filter-row">
        <input
          className="admin-search-input"
          placeholder="이메일 또는 이름 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          className="admin-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">전체 권한</option>
          <option value="user">일반</option>
          <option value="admin">관리자</option>
        </Select>
        {/* 플랜 필터·열 숨김 (2026-10-19 김현비) — 요금제를 닫아 플랜 구분이 의미 없다 */}
        <Select
          className="admin-select"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="desc">최신 가입순</option>
          <option value="asc">오래된 가입순</option>
        </Select>
        <span className="admin-count">{filtered.length}명</span>
      </div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>이메일</th>
              <th>이름</th>
              <th>로그인</th>
              <th>권한</th>
              <th>상태</th>
              <th>가입일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className={u.is_blocked ? "blocked-row" : ""}>
                <td>{u.email}</td>
                <td>{u.username || "-"}</td>

                {/* 로그인 방식 */}
                <td>
                  {u.provider && u.provider !== "email" ? (
                    <span className="admin-badge blue">
                      {u.provider === "google" ? "Google" : u.provider}
                    </span>
                  ) : (
                    <span className="admin-badge">Email</span>
                  )}
                </td>

                {/* 권한 셀렉트 */}
                <td>
                  <Select
                    className="admin-select"
                    value={u.role}
                    onChange={async (e) => {
                      const role = e.target.value;
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        const res = await fetch(`${API_BASE}/admin/update-role`, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${session.access_token}`,
                          },
                          body: JSON.stringify({ target_user_id: u.id, role }),
                        });
                        if (!res.ok) {
                          showAlert("권한 변경 실패");
                          return;
                        }
                        setUsers(
                          users.map((x) =>
                            x.id === u.id ? { ...x, role } : x
                          )
                        );
                      } catch (err) {
                        console.error(err);
                        showAlert("서버 오류");
                      }
                    }}
                  >
                    <option value="user">일반</option>
                    <option value="admin">관리자</option>
                  </Select>
                </td>


                {/* 차단 상태 */}
                <td>
                  {u.is_blocked ? (
                    <span className="admin-badge red">차단</span>
                  ) : (
                    <span className="admin-badge green">정상</span>
                  )}
                </td>

                {/* 가입일 */}
                <td>
                  {u.created_at
                    ? new Date(u.created_at).toLocaleDateString()
                    : "-"}
                </td>

                {/* 관리 버튼 */}
                <td>
                  <div className="admin-action-btns">
                    <button className="admin-btn" onClick={() => loadUserDetail(u)}>상세</button>
                    <button
                      className={`admin-btn ${u.is_blocked ? "approve" : "reject"}`}
                      onClick={() => toggleBlock(u.id, u.is_blocked)}
                    >
                      {u.is_blocked ? "해제" : "차단"}
                    </button>
                    <button
                      className="admin-btn danger"
                      onClick={async () => {
                        if (
                          !(await showConfirm(
                            "정말로 이 유저를 탈퇴 처리하시겠습니까?"
                          ))
                        )
                          return;
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          const res = await fetch(`${API_BASE}/admin/delete-user`, {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: `Bearer ${session.access_token}`,
                            },
                            body: JSON.stringify({ target_user_id: u.id }),
                          });
                          if (!res.ok) {
                            showAlert("탈퇴 처리 실패");
                            return;
                          }
                          setUsers(users.filter((x) => x.id !== u.id));
                        } catch (e) {
                          console.error(e);
                          showAlert("서버 오류");
                        }
                      }}
                    >
                      탈퇴
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* 유저 상세 모달 */}
      {selectedUser && (
        <div className="ao-modal-overlay" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>
          <div className="ao-modal" onClick={(e) => e.stopPropagation()} style={{ width: 600 }}>
            <h3>유저 상세</h3>
            <button className="ao-modal-close" onClick={() => { setSelectedUser(null); setUserDetail(null); }}>✕</button>

            <div className="ao-modal-body">
            <div className="ao-detail-sections">
              {/* 기본 정보 */}
              <div className="ao-detail-group">
                <h4>기본 정보</h4>
                <div className="ao-detail-grid">
                  <div><strong>이메일</strong><span>{selectedUser.email}</span></div>
                  <div><strong>이름</strong><span>{selectedUser.username || "-"}</span></div>
                  <div><strong>가입일</strong><span>{new Date(selectedUser.created_at).toLocaleDateString("ko-KR")}</span></div>
                  <div><strong>로그인</strong><span>{selectedUser.provider === "google" ? "Google" : "Email"}</span></div>
                  <div><strong>권한</strong><span>{selectedUser.role === "admin" ? "관리자" : "일반"}</span></div>
                  <div><strong>상태</strong><span style={{ color: selectedUser.is_blocked ? "#ef4444" : "#10b981", fontWeight: 600 }}>{selectedUser.is_blocked ? "차단" : "정상"}</span></div>
                </div>
              </div>

              {detailLoading ? (
                <div style={{ textAlign: "center", padding: 20, color: "#888" }}>로딩 중...</div>
              ) : userDetail && (
                <>
                  {/* 저장공간 */}
                  <div className="ao-detail-group">
                    <h4>저장공간</h4>
                    <div style={{ fontSize: 13, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                      <span><strong>{userDetail.storage.usedMb} MB</strong> / {userDetail.storage.limitMb > 0 ? `${userDetail.storage.limitMb} MB` : "무제한"}</span>
                      <span style={{ color: "#999" }}>{userDetail.storage.fileCount}개 파일</span>
                    </div>
                    {userDetail.storage.limitMb > 0 && (
                      <div style={{ height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{
                          height: "100%",
                          width: `${Math.min(100, (userDetail.storage.usedMb / userDetail.storage.limitMb) * 100)}%`,
                          background: userDetail.storage.usedMb / userDetail.storage.limitMb > 0.9 ? "#ef4444" : "#C8813F",
                          borderRadius: 4, transition: "width 0.3s",
                        }} />
                      </div>
                    )}
                  </div>

                  {/* 저장된 배송지 */}
                  <div className="ao-detail-group">
                    <h4>저장된 배송지 ({userDetail.addresses.length})</h4>
                    {userDetail.addresses.length === 0 ? (
                      <p style={{ fontSize: 13, color: "#999", margin: 0 }}>저장된 배송지가 없습니다.</p>
                    ) : userDetail.addresses.map((a) => (
                      <div key={a.id} className="au-addr-item">
                        <div className="au-addr-title">
                          <strong>{a.label}</strong>
                          {a.is_default && <span className="au-default-badge">기본</span>}
                        </div>
                        <div className="au-addr-sub">{a.customer_name} · {a.customer_phone}</div>
                        <div className="au-addr-sub">{a.zipcode && `(${a.zipcode}) `}{a.address} {a.address_detail}</div>
                      </div>
                    ))}
                  </div>

                  {/* 주문 내역 */}
                  <div className="ao-detail-group">
                    <h4>주문 내역 ({userDetail.orders.length})</h4>
                    {userDetail.orders.length === 0 ? (
                      <p style={{ fontSize: 13, color: "#999", margin: 0 }}>주문 내역이 없습니다.</p>
                    ) : userDetail.orders.map((o) => (
                      <div key={o.id} className="au-order-item" onClick={() => setSelectedOrder(o)} style={{ cursor: "pointer" }}>
                        <div className="au-order-top">
                          <span className="au-order-num">{o.order_number || "-"}</span>
                          <span className="au-order-date">{new Date(o.created_at).toLocaleDateString("ko-KR")}</span>
                          <span className="ao-badge" style={{ background: STATUS_COLORS[o.status], marginLeft: "auto" }}>
                            {STATUS_MAP[o.status] || o.status}
                          </span>
                        </div>
                        <div className="au-order-price">{((o.total_price || 0) + (o.shipping_cost || 0)).toLocaleString()}원</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* 주문 상세 모달 */}
      {selectedOrder && (
        <div className="ao-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="ao-modal" onClick={(e) => e.stopPropagation()}>
            <h3>주문 상세</h3>
            <button className="ao-modal-close" onClick={() => setSelectedOrder(null)}>✕</button>
            <div className="ao-modal-body">
              <div className="ao-detail-sections">
                <div className="ao-detail-group">
                  <h4>주문 정보</h4>
                  <div className="ao-detail-grid">
                    <div><strong>주문번호</strong><span>{selectedOrder.order_number || "-"}</span></div>
                    <div><strong>주문일</strong><span>{new Date(selectedOrder.created_at).toLocaleString("ko-KR")}</span></div>
                    <div><strong>고객명</strong><span>{selectedOrder.customer_name}</span></div>
                    <div><strong>연락처</strong><span>{selectedOrder.customer_phone}</span></div>
                    <div><strong>상태</strong><span><span className="ao-badge" style={{ background: STATUS_COLORS[selectedOrder.status] }}>{STATUS_MAP[selectedOrder.status] || selectedOrder.status}</span></span></div>
                  </div>
                </div>
                <div className="ao-detail-group">
                  <h4>출력 옵션</h4>
                  <div className="ao-detail-grid">
                    <div><strong>색상</strong><span>{selectedOrder.color}</span></div>
                    <div><strong>밀도</strong><span>{selectedOrder.infill}%</span></div>
                    <div><strong>수량</strong><span>{selectedOrder.quantity}</span></div>
                    {selectedOrder.memo && <div><strong>요청사항</strong><span>{selectedOrder.memo}</span></div>}
                  </div>
                </div>
                <div className="ao-detail-group">
                  <h4>배송 정보</h4>
                  <div className="ao-detail-grid">
                    <div><strong>배송유형</strong><span>{selectedOrder.shipping_type === "domestic" ? "국내배송" : "해외배송"}</span></div>
                    <div><strong>주소</strong><span>{selectedOrder.zipcode && `(${selectedOrder.zipcode}) `}{selectedOrder.address} {selectedOrder.address_detail}</span></div>
                    {selectedOrder.delivery_memo && <div><strong>배송메모</strong><span>{selectedOrder.delivery_memo}</span></div>}
                    {selectedOrder.tracking_number && <div><strong>송장번호</strong><span>{selectedOrder.tracking_number}</span></div>}
                  </div>
                </div>
                <div className="ao-detail-group">
                  <h4>결제 정보</h4>
                  <div className="ao-detail-grid">
                    <div><strong>출력 비용</strong><span>{selectedOrder.total_price ? `${selectedOrder.total_price.toLocaleString()}원` : "-"}</span></div>
                    <div><strong>배송비</strong><span>{selectedOrder.shipping_cost ? `${selectedOrder.shipping_cost.toLocaleString()}원` : "-"}</span></div>
                  </div>
                  <div className="ao-detail-total">
                    <strong>총 결제 금액</strong>
                    <span>{((selectedOrder.total_price || 0) + (selectedOrder.shipping_cost || 0)).toLocaleString()}원</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
